import React, {
  useEffect, useReducer, useState, useCallback, useRef,
} from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { getActiveLocaleSelector } from '@americanexpress/travel-selectors';
import { Settings } from 'luxon';
import { THEMES, useMediaBreakpoint } from '@americanexpress/consumer-travel-base';
import { Surface } from '@americanexpress/dls-react';
import clsx from 'clsx';
import PopupDesktop from './PopupDesktop/PopupDesktop';
import PopupMobile from './PopupMobile/PopupMobile';
import DateContext from '../context/dateContext';
import TimeContext from '../context/timeContext';
import ThemeContext from '../context/themeContext';
import {
  dateReducer,
  initialDateState,
  setIsDateRange,
} from '../ducks/dateDuck';
import {
  timeReducer, initialTimeState,
} from '../ducks/timeDuck';
import Popup from './Popup/Popup';
import InputMasks from './InputMasks/InputMasks';
import {
  useAllPossibleDates,
  useCalendarData,
  useParentTimeValue,
  useUpdateParentWhenStateChanges,
  useUpdateStateWhenParentDateValueChanges,
} from '../hooks/travelSearchDatePickerHooks';
import InlineCalendar from './InlineCalendar/InlineCalendar';
import { useIsHomePageVariantHook } from '../hooks/useIsHomePageVariantHook';

export const getMarginByLocation = ({ pathname } = {}) => {
  if (pathname === '/demo/axp-travel-search-date-picker') {
    return 'margin';
  }
  return 'margin-0';
};

const TravelSearchDatePicker = ({
  value: parentValue,
  onChange,
  isDateRange,
  placeholder,
  hasTimeDropdown,
  theme,
  minStartDate: parentMinStartDate,
  maxDaysAdvanceFromToday: parentMaxDaysAdvanceFromToday,
  minNights,
  maxNights,
  is24Hour,
  hasAnytime,
  minutesIntervalPerOption,
  warnings: {
    isStartDateWarning,
    isEndDateWarning,
  },
  location,
  defaultTime,
  onClick,
  isInline,
  isMinStayVisible,
  onClose,
  datesAvailability,
  minNightsPerDate,
  pricePerDate,
  isOutSideClickCloseEnabled,
  isEnableCloseButtonForDesktop,
  isEnabledNewTimeLabel,
  errors,
}) => {
  const locale = useSelector(getActiveLocaleSelector);
  const [isOpen, setIsOpen] = useState(false);
  const [timeState, timeDispatch] = useReducer(timeReducer, {
    ...initialTimeState,
    startTime: parentValue.startTime,
    endTime: parentValue.endTime,
  });
  const [dateState, dateDispatch] = useReducer(dateReducer, {
    ...initialDateState,
    isDateRange,
    startDate: parentValue.startDate,
    endDate: parentValue.endDate,
  });
  const { startDate, endDate } = dateState;
  const { startTime, endTime } = timeState;

  useAllPossibleDates({
    parentMaxDaysAdvanceFromToday,
    parentMinStartDateString: parentMinStartDate,
    dateDispatch,
  });

  useCalendarData({
    dateState,
    parentMaxDaysAdvanceFromToday,
    minNights,
    maxNights,
    dateDispatch,
    datesAvailability,
    minNightsPerDate,
    pricePerDate,
    isInline,
  });

  useEffect(() => {
    Settings.defaultLocale = locale;
  }, [locale]);

  useEffect(() => {
    if (dateState.isDateRange !== isDateRange) {
      dateDispatch(setIsDateRange(isDateRange));
    }
  }, [isDateRange, dateState.isDateRange]);

  useUpdateParentWhenStateChanges({
    startDate,
    endDate,
    startTime,
    endTime,
    parentValue,
    hasTimeDropdown,
    onChange,
  });

  useUpdateStateWhenParentDateValueChanges({
    parentValue,
    startDate,
    endDate,
    dateDispatch,
  });

  useParentTimeValue({
    parentValue,
    timeDispatch,
    startTime,
    endTime,
  });

  const handleTooltipClose = useCallback(() => {
    setIsOpen(false);
    onClose();
  }, []);

  const startDateRef = useRef();
  const endDateRef = useRef();
  const isMobile = useMediaBreakpoint('sm-down');
  const isVariantRedesign = useIsHomePageVariantHook();

  const surfaceWrapper = (children) => {
    if (theme === THEMES.THEME_BLUE) {
      return (
        <Surface variant="fg-brand" data-testid="date-picker-surface" className={clsx('position-relative flex-align-end', getMarginByLocation(location))}>
          {children}
        </Surface>
      );
    } if (isVariantRedesign) {
      return (
        <div data-testid="date-picker-surface" className={clsx('position-relative flex-align-end', getMarginByLocation(location))}>
          {children}
        </div>
      );
    }
    return (
      <Surface variant="foreground" data-testid="date-picker-surface" className={clsx('position-relative flex-align-end', getMarginByLocation(location))}>
        {children}
      </Surface>
    );
  };

  return (
    // eslint-disable-next-line react/jsx-no-constructed-context-values -- expected for provider
    <DateContext.Provider value={{
      state: dateState,
      dispatch: dateDispatch,
      props: {
        isStartDateWarning,
        isEndDateWarning,
        isMinStayVisible,
        isOutSideClickCloseEnabled,
        isEnableCloseButtonForDesktop,
      },
    }}
    >
      <ThemeContext.Provider value={theme}>
        {/* eslint-disable-next-line react/jsx-no-constructed-context-values -- expected */}
        <TimeContext.Provider value={{
          state: timeState,
          dispatch: timeDispatch,
          props: {
            hasTimeDropdown,
            is24Hour,
            hasAnytime,
            minutesIntervalPerOption,
            defaultTime,
            isEnabledNewTimeLabel,
          },
        }}
        >
          <div>{
            isInline ? (
              <InlineCalendar
                placeholder={placeholder}
                onClose={onClose}
              />
            )
              : surfaceWrapper(
                <>
                  <InputMasks
                    isOpen={isOpen}
                    setIsOpen={setIsOpen}
                    placeholder={placeholder}
                    startDateRef={startDateRef}
                    endDateRef={endDateRef}
                    onClick={onClick}
                    errors={errors}
                  />
                  <Popup
                    isOpen={isOpen}
                    onClose={handleTooltipClose}
                    startDateRef={startDateRef}
                    endDateRef={endDateRef}
                  >
                    {isMobile
                      ? (
                        <PopupMobile
                          onClosePopup={handleTooltipClose}
                          placeholder={placeholder}
                        />
                      )
                      : (
                        <PopupDesktop
                          onClosePopup={handleTooltipClose}
                          placeholder={placeholder}
                        />
                      )}
                  </Popup>
                </>)

          }
          </div>
        </TimeContext.Provider>
      </ThemeContext.Provider>
    </DateContext.Provider>
  );
};

TravelSearchDatePicker.propTypes = {
  value: PropTypes.shape({
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    startTime: PropTypes.string,
    endTime: PropTypes.string,
  }),
  onChange: PropTypes.func,
  isDateRange: PropTypes.bool,
  hasTimeDropdown: PropTypes.bool,
  placeholder: PropTypes.shape({
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    ariaStartDate: PropTypes.string,
    ariaEndDate: PropTypes.string,
  }),
  maxDaysAdvanceFromToday: PropTypes.number,
  minNights: PropTypes.number,
  maxNights: PropTypes.number,
  minStartDate: PropTypes.string,
  minNightsPerDate: PropTypes.objectOf(PropTypes.string),
  pricePerDate: PropTypes.objectOf(PropTypes.string),
  theme: PropTypes.oneOf([THEMES.THEME_BLUE, THEMES.THEME_WHITE]),
  datesAvailability: PropTypes.shape({}),
  // eslint-disable-next-line react/boolean-prop-naming -- it does match naming scheme
  is24Hour: PropTypes.bool,
  hasAnytime: PropTypes.bool,
  minutesIntervalPerOption: PropTypes.oneOf([10, 15, 30, 60]),
  warnings: PropTypes.shape({
    isStartDateWarning: PropTypes.bool,
    isEndDateWarning: PropTypes.bool,
  }),
  isInline: PropTypes.bool,
  defaultTime: PropTypes.string,
  onClick: PropTypes.func,
  isMinStayVisible: PropTypes.bool,
  onClose: PropTypes.func,
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }),
  isOutSideClickCloseEnabled: PropTypes.bool,
  isEnableCloseButtonForDesktop: PropTypes.bool,
  isEnabledNewTimeLabel: PropTypes.bool,
  errors: PropTypes.shape({
    startDateErrors: PropTypes.arrayOf(PropTypes.string),
    endDateErrors: PropTypes.arrayOf(PropTypes.string),
  }),
};

export const emptyFunction = () => ({});

TravelSearchDatePicker.defaultProps = {
  value: {
    startDate: null,
    endDate: null,
    startTime: null,
    endTime: null,
  },
  onChange: emptyFunction,
  isDateRange: true,
  hasTimeDropdown: false,
  placeholder: {
    startDate: 'Start Date',
    endDate: 'End Date',
  },
  maxDaysAdvanceFromToday: 300,
  minStartDate: null,
  minNights: 0,
  maxNights: 300,
  minNightsPerDate: {},
  pricePerDate: null,
  datesAvailability: null,
  is24Hour: true,
  theme: THEMES.THEME_WHITE,
  hasAnytime: true,
  minutesIntervalPerOption: 60,
  warnings: {
    isStartDateWarning: false,
    isEndDateWarning: false,
  },
  isInline: false,
  defaultTime: '11:00',
  onClick: emptyFunction,
  isMinStayVisible: false,
  onClose: emptyFunction,
  location: {},
  isOutSideClickCloseEnabled: true,
  isEnableCloseButtonForDesktop: false,
  isEnabledNewTimeLabel: false,
};

export default TravelSearchDatePicker;
